/**
 * src/components/directory-page/BusinessCard.js
 *
 * Tarjeta de negocio reutilizable para Plan Oro.
 * Props: { id, name, category, description, image, location,
 *          whatsapp, rating, reviews, badge, badgeColor,
 *          open, hasPromo, plan }
 */

import { define } from '@components/Component.js'
import { h } from '@core/dom.js'
import { signal, effect } from '@core/signal.js'
import { trackClick } from '@/shared/utils/track.js'
import { uiIcons } from '@/shared/brandIcons.js'
import { waLink } from '@/shared/utils/whatsapp.js'
import { getReviewStats, getBusinessStats } from '@/services/reviewStats.js'

const BADGE_COLORS = {
    green:  '#2d8a4e',
    gold:   '#b7791f',
    blue:   '#3b82f6',
    purple: '#7c3aed',
    orange: '#f97316',
    teal:   '#0d9488'
}

const stars = rating => {
    const full  = Math.floor(rating)
    const half  = rating % 1 >= 0.5 ? 1 : 0
    const empty = 5 - full - half
    return '★'.repeat(full) + (half ? '½' : '') + '☆'.repeat(empty)
}

define('BusinessCard', (props) => {
    const {
        id, name, category, description, image, location,
        whatsapp, rating, reviews, badge, badgeColor,
        open, hasPromo, plan, views
    } = props

    const liked = signal(false)

    /* ── Imagen ── */
    const imgWrap = h('a', { className: 'biz-card__img-wrap', href: `/negocio/${id}` },
        h('img', { src: image, alt: name, className: 'biz-card__img' }),
        h('div', { className: 'biz-card__img-overlay' })
    )

    /* Badge */
    if (badge) {
        const badgeEl = h('span', { className: 'biz-card__badge' }, badge)
        badgeEl.style.background = BADGE_COLORS[badgeColor] || BADGE_COLORS.green
        imgWrap.appendChild(badgeEl)
    }

    /* Plan tag */
    if (plan === 'oro') {
        imgWrap.appendChild(h('span', { className: 'biz-card__plan biz-card__plan--oro', innerHTML: uiIcons.star + ' Oro' }))
    }

    /* Open/closed pill */
    imgWrap.appendChild(
        h('span', { className: `biz-card__open biz-card__open--${open ? 'yes' : 'no'}` },
            open ? 'Abierto' : 'Cerrado'
        )
    )

    /* Favorito */
    const heartBtn = h('button', {
        className: 'biz-card__heart',
        type: 'button',
        'aria-label': 'Guardar favorito'
    }, '♡')

    heartBtn.addEventListener('click', () => liked.set(!liked.get()))
    effect(() => {
        heartBtn.textContent = liked.get() ? '♥' : '♡'
        heartBtn.classList.toggle('biz-card__heart--active', liked.get())
    })
    imgWrap.appendChild(heartBtn)

    /* Promo badge */
    if (hasPromo) {
        imgWrap.appendChild(h('span', { className: 'biz-card__promo' }, 'Promo'))
    }

    /* ── Cuerpo ── */
    const body = h('div', { className: 'biz-card__body' },
        h('p',  { className: 'biz-card__category' }, category),
        h('h3', { className: 'biz-card__name' }, name),
        h('p',  { className: 'biz-card__desc' }, description),
        h('div', { className: 'biz-card__meta' },
            h('span', { className: 'biz-card__location', innerHTML: uiIcons.pin + ' ' + location }),
            views ? h('span', { className: 'biz-card__views' }, `👁 ${views} visitas`) : null
        ),
        (() => {
            const ratingEl = h('div', { className: 'biz-card__rating' },
                h('span', { className: 'biz-card__stars' }, stars(0)),
                h('span', { className: 'biz-card__rating-num' }, '—'),
                h('span', { className: 'biz-card__reviews' }, '(0)')
            )
            getReviewStats().then(stats => {
                const s = getBusinessStats(stats, id)
                ratingEl.innerHTML = ''
                ratingEl.appendChild(h('span', { className: 'biz-card__stars' }, stars(s.avg)))
                ratingEl.appendChild(h('span', { className: 'biz-card__rating-num' }, s.avg ? String(s.avg) : '—'))
                ratingEl.appendChild(h('span', { className: 'biz-card__reviews' }, `(${s.count})`))
            })
            return ratingEl
        })(),
        h('div', { className: 'biz-card__actions' },
            h('a', {
                className: 'biz-card__btn biz-card__btn--outline',
                href: `/negocio/${id}`
            }, 'Ver negocio'),
            (() => {
                const wa = h('a', {
                    className: 'biz-card__btn biz-card__btn--whatsapp',
                    href: waLink(whatsapp),
                    target: '_blank',
                    rel: 'noopener',
                    innerHTML: uiIcons.whatsapp + ' WhatsApp' })
                wa.addEventListener('click', () => trackClick('business', id))
                return wa
            })()
        )
    )

    return h('article', { className: `biz-card biz-card--${plan}` }, imgWrap, body)
})
